import type { ComponentPropsWithoutRef, ElementType } from 'react'

import type { BaseButtonProps, ButtonColor, ButtonSize } from '@gto/ui/button'
import { Button } from '@gto/ui/button'
import { cn } from '@gto/ui/cn'

type IconButtonEdge = 'start' | 'end'

type IconButtonColor = ButtonColor | 'tertiary'

type IconButtonShape = 'round' | 'square'

type IconButtonOwnProps = Omit<BaseButtonProps, 'startIcon' | 'endIcon' | 'fullWidth' | 'color'> & {
  color?: IconButtonColor
  edge?: IconButtonEdge
  /** Corner style — `'round'` (default) renders a pill/circle, `'square'` renders the Figma "Square Button" with `rounded-sm` corners. */
  shape?: IconButtonShape
  as?: ElementType
  component?: ElementType
  href?: string
  target?: string
  rel?: string
}

type IconButtonProps = IconButtonOwnProps &
  Omit<ComponentPropsWithoutRef<'button'>, keyof IconButtonOwnProps>

const ICON_BUTTON_SIZE_CLASSES: Record<ButtonSize, string> = {
  small: 'size-6 p-0.5 [&>svg]:size-5',
  medium: 'size-8 p-1 [&>svg]:size-6',
  large: 'size-12 p-3 [&>svg]:size-6 min-w-12',
}

const ICON_BUTTON_EDGE_CLASSES: Record<IconButtonEdge, Record<ButtonSize, string>> = {
  start: {
    small: '-ml-0.5',
    medium: '-ml-1',
    large: '-ml-3',
  },
  end: {
    small: '-mr-0.5',
    medium: '-mr-1',
    large: '-mr-3',
  },
}

const ICON_BUTTON_SHAPE_CLASSES: Record<IconButtonShape, string> = {
  // The focus ring (`::after`) follows the round shape. Left at the Button's 4px corner,
  // a keyboard focus drew a square ring inside a round button.
  round: 'rounded-full after:rounded-full',
  square: 'rounded-sm',
}

const IconButton = ({
  size = 'medium',
  variant = 'text',
  color = 'secondary',
  shape = 'round',
  loading = false,
  edge,
  className,
  children,
  disabled = false,
  ...rest
}: IconButtonProps) => {
  const isTertiary = color === 'tertiary'
  const buttonColor = isTertiary ? 'secondary' : color

  return (
    <Button
      color={buttonColor}
      disabled={disabled}
      loading={loading}
      size={size}
      variant={variant}
      {...rest}
      className={cn(
        ICON_BUTTON_SHAPE_CLASSES[shape],
        ICON_BUTTON_SIZE_CLASSES[size],
        edge && ICON_BUTTON_EDGE_CLASSES[edge][size],
        isTertiary && !disabled && !loading && 'text-text-action-active',
        className,
      )}
    >
      {!loading && children}
    </Button>
  )
}

export { IconButton }
export type { IconButtonProps, IconButtonShape }
